import {
  STATUS_ICON_CHECK_O,
  STATUS_ICON_LOADING,
  STATUS_ICON_TIMES_O,
  STATUS_ICON_INACTIVE,
  STATUS_ICON_CLOCK,
  STATE_ICON_RUN,
  STATE_ICON_STOP,
  STATE_ICON_PAUSE
} from '@/common/utils/statuses';
import computedLocale from '@/common/utils/computedLocale';
import i18n from '@/common/i18n';
import { MigrationStatus } from '@schema';
import { VM_STATUS, type VMStatus } from '@/common/store/pinia/migrationClouds/cloudInstancesMapper';

export { MigrationStatus };

export const MIGRATION_STATUSES_HIDDEN_IN_SSP: readonly string[] = [
  MigrationStatus.CANCELED_FOR_DEBUGGING,
  MigrationStatus.DEADLOCKED
];

export const MIGRATION_STATUS_TEXT = computedLocale<Record<MigrationStatus, string>>(() => ({
  [MigrationStatus.RUNNING]: i18n.t('Running'),
  [MigrationStatus.UNEXECUTED]: i18n.t('Pending'),
  [MigrationStatus.REPLICATING]: i18n.t('Replicating'),
  [MigrationStatus.REPLICATED]: i18n.t('Replicated'),
  [MigrationStatus.CANCELLING]: i18n.t('Canceling'),
  [MigrationStatus.ERROR]: i18n.t('Error'),
  [MigrationStatus.CANCELED]: i18n.t('Canceled'),
  [MigrationStatus.CANCELED_FOR_DEBUGGING]: i18n.t('Canceled for debugging'),
  [MigrationStatus.DEADLOCKED]: i18n.t('Deadlocked'),
  [MigrationStatus.DEPLOYING]: i18n.t('Deploying'),
  [MigrationStatus.DEPLOYED]: i18n.t('Deployed'),
  [MigrationStatus.COMPLETED]: i18n.t('Completed')
}));

export const MIGRATION_STATUS_ICON: Record<MigrationStatus, string> = {
  [MigrationStatus.RUNNING]: STATUS_ICON_LOADING,
  [MigrationStatus.UNEXECUTED]: STATUS_ICON_CLOCK,
  [MigrationStatus.REPLICATING]: STATUS_ICON_LOADING,
  [MigrationStatus.REPLICATED]: STATUS_ICON_CHECK_O,
  [MigrationStatus.CANCELLING]: STATUS_ICON_LOADING,
  [MigrationStatus.ERROR]: STATUS_ICON_TIMES_O,
  [MigrationStatus.CANCELED]: STATUS_ICON_INACTIVE,
  [MigrationStatus.CANCELED_FOR_DEBUGGING]: STATUS_ICON_INACTIVE,
  [MigrationStatus.DEADLOCKED]: STATUS_ICON_TIMES_O,
  [MigrationStatus.DEPLOYING]: STATUS_ICON_LOADING,
  [MigrationStatus.DEPLOYED]: STATUS_ICON_CHECK_O,
  [MigrationStatus.COMPLETED]: STATUS_ICON_CHECK_O
};

export const MIGRATION_DISK_BUS_DEFAULT = 'virtio' as const;

export const MIGRATION_DISK_BUS_OPTIONS = [
  { value: 'scsi', label: 'SCSI' },
  { value: 'ide', label: 'IDE' },
  { value: 'virtio', label: 'VirtIO' }
] as const;

export const MIGRATION_DISK_BUS_LABELS: Record<string, string> = Object.fromEntries(
  MIGRATION_DISK_BUS_OPTIONS.map(({ value, label }) => [value, label])
);

/** `os_type` from the migration-cloud API; matches `os_distributions[].os_type` on the target cluster. */
export const MIGRATION_VM_OS_TYPE = {
  LINUX: 'linux',
  WINDOWS: 'windows',
  OTHER: 'other',
} as const;

export const MIGRATION_VM_OS_TYPE_LABELS = computedLocale<Record<string, string>>(() => ({
  [MIGRATION_VM_OS_TYPE.LINUX]: i18n.t('Linux'),
  [MIGRATION_VM_OS_TYPE.WINDOWS]: i18n.t('Windows'),
  [MIGRATION_VM_OS_TYPE.OTHER]: i18n.t('Other'),
}));

export const MIGRATION_CLOUD_INSTANCE_STATE_LABELS = computedLocale<Record<VMStatus, string>>(() => ({
  [VM_STATUS.POWERED_ON]: i18n.t('Active'),
  [VM_STATUS.POWERED_OFF]: i18n.t('Stopped'),
  [VM_STATUS.SUSPENDED]: i18n.t('Suspended'),
}));

export const MIGRATION_CLOUD_INSTANCE_STATUS_ICONS: Record<VMStatus, string> = {
  [VM_STATUS.POWERED_ON]: STATE_ICON_RUN,
  [VM_STATUS.POWERED_OFF]: STATE_ICON_STOP,
  [VM_STATUS.SUSPENDED]: STATE_ICON_PAUSE
};

export const MIGRATION_MACHINE_TYPE_OPTIONS = [
  { value: 'pc', label: 'pc-i440fx' },
  { value: 'q35', label: 'pc-q35' }
] as const;

export const MIGRATION_MACHINE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  MIGRATION_MACHINE_TYPE_OPTIONS.map(({ value, label }) => [value, label])
);
